import React from 'react'
import { useFilters } from 'components/charts/FiltersContext'
import { Doughnut } from 'react-chartjs-2'
import 'chartjs-plugin-doughnutlabel2'
import {
  Row, Col, Card, CardBody, CardTitle,
} from 'reactstrap'
import Spinner from 'components/common/Loader'
import { gql, useQuery } from '@apollo/client'
import { Query } from 'generated/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { colors, chartColors, themeColors } from 'components/charts/colors'
import moment from 'moment'

// this is the number of days to look back for survey data in charts
const SLIDING_WINDOW_DAYS = 7

const USER_COUNT = gql`
  query UserCount($tags: [String!], $asOf: DateTime!) {
    chart {
      userCount(filters: { tags: $tags, asOf: $asOf })
    }
  }
`

const PARTICIPATED_USER_COUNT = gql`
  query ParticipatedUserCount($tags: [String!], $startDate: DateTime!, $endDate: DateTime!) {
    chart {
      completedSurveyCount(filters: { tags: $tags, startDate: $startDate, endDate: $endDate })
    }
  }
`

const REPORTED_SYMPTOMS_USER_COUNT = gql`
  query ReportedSymptomsCount($tags: [String!], $startDate: DateTime!, $endDate: DateTime!) {
    chart {
      reportedSymptomsCount(filters: { tags: $tags, startDate: $startDate, endDate: $endDate })
    }
  }
`

const NO_SYMPTOMS_USER_COUNT = gql`
  query NoSymptomsCount($tags: [String!], $startDate: DateTime!, $endDate: DateTime!) {
    chart {
      noSymptomsCount(filters: { tags: $tags, startDate: $startDate, endDate: $endDate })
    }
  }
`

const GO_TO_OFFICE = gql`
  query GoToOffice($tags: [String!], $startDate: DateTime!, $endDate: DateTime!) {
    questionBySlug(slug: "go-to-office") {
      id
      slug
      mostRecentResponses(filters: { tags: $tags, startDate: $startDate, endDate: $endDate }) {
        answers {
          id
          option {
            id
            slug
          }
        }
      }
    }
  }
`

const initialTeamParticipation = {
  datasets: [
    {
      data: [0, 0],
      backgroundColor: [chartColors.medium, chartColors.neutral],
      borderWidth: 0,
    },
  ],
  labels: ['completed 1+ survey(s) in last week', 'no completed surveys in last week'],
}

const initialTeamHealth = {
  datasets: [
    {
      data: [0, 0],
      backgroundColor: [colors[2], chartColors.neutral],
      borderWidth: 0,
    },
  ],
  labels: ['1+ symptoms reported', 'no symptoms reported'],
}

const initialGoToOffice = {
  datasets: [
    {
      data: [0, 0],
      backgroundColor: [colors[1], chartColors.neutral],
      borderWidth: 0,
    },
  ],
  labels: ['desire to visit office', ''],
}

const DailySnapshot = () => {
  const [filters] = useFilters()

  const yesterday = moment().subtract(1, 'day').startOf('day')

  const userCountQuery = useQuery<Query>(USER_COUNT, {
    variables: { tags: filters.tags, asOf: yesterday },
  })
  const userCount = userCountQuery.data?.chart?.userCount || 0

  const startDate = yesterday.clone().subtract(SLIDING_WINDOW_DAYS, 'days')

  const participatedUserCount = useQuery<Query>(PARTICIPATED_USER_COUNT, {
    variables: { tags: filters.tags, startDate: startDate.toDate(), endDate: yesterday.toDate() },
  })

  const reportedSymptomsUserCount = useQuery<Query>(REPORTED_SYMPTOMS_USER_COUNT, {
    variables: { tags: filters.tags, startDate: startDate.toDate(), endDate: yesterday.toDate() },
  })

  const noSymptomsUserCount = useQuery<Query>(NO_SYMPTOMS_USER_COUNT, {
    variables: { tags: filters.tags, startDate: startDate.toDate(), endDate: yesterday.toDate() },
  })

  const goToOffice = useQuery<Query>(GO_TO_OFFICE, {
    variables: { tags: filters.tags, startDate: startDate.toDate(), endDate: yesterday.toDate() },
  })

  const teamParticipationLoading = (): boolean => userCountQuery.loading || participatedUserCount.loading

  const teamParticipationError = (): string | undefined => {
    let error = ''

    if (userCountQuery.error) {
      error = `userCount error: ${userCountQuery.error}`
    }

    if (participatedUserCount.error) {
      const msg = `participatedUserCount error: ${participatedUserCount.error}`
      error = error.length > 0 ? `; ${msg}` : msg
    }

    return error.length > 0 ? error : undefined
  }

  const teamParticipationReady = (): boolean => !teamParticipationLoading() && !teamParticipationError()

  const teamParticipationData = () => {
    const data = { ...initialTeamParticipation }
    const participated = participatedUserCount.data!.chart.completedSurveyCount
    data.datasets[0].data[0] = participated
    data.datasets[0].data[1] = Math.max(0, userCount - participated)
    return data
  }

  const teamHealthLoading = (): boolean => reportedSymptomsUserCount.loading || noSymptomsUserCount.loading

  const teamHealthError = (): string | undefined => {
    let error = ''

    if (reportedSymptomsUserCount.error) {
      const msg = `reportedSymptomsUserCount error: ${reportedSymptomsUserCount.error}`
      error = error.length > 0 ? `; ${msg}` : msg
    }

    if (noSymptomsUserCount.error) {
      const msg = `noSymptomsUserCount error: ${noSymptomsUserCount.error}`
      error = error.length > 0 ? `; ${msg}` : msg
    }

    return error.length > 0 ? error : undefined
  }

  const teamHealthReady = (): boolean => !teamHealthLoading() && !teamHealthError()

  const teamHealthData = () => {
    const data = { ...initialTeamHealth }
    data.datasets[0].data[0] = reportedSymptomsUserCount.data!.chart.reportedSymptomsCount
    data.datasets[0].data[1] = noSymptomsUserCount.data!.chart.noSymptomsCount
    return data
  }

  const goToOfficeLoading = (): boolean => goToOffice.loading

  const goToOfficeError = (): string | undefined => {
    let error = ''
    if (goToOffice.error) {
      error = `${goToOffice.error}`
    }

    return error.length > 0 ? error : undefined
  }

  const goToOfficeReady = (): boolean => (!goToOfficeLoading() && !goToOfficeError())

  const goToOfficeData = () => {
    const data = { ...initialGoToOffice }
    data.datasets[0].data[0] = (goToOffice?.data?.questionBySlug?.mostRecentResponses || []).filter((response) => {
      return (response?.answers[0].option?.slug === 'yes')
    }).length
    data.datasets[0].data[1] = userCount
    return data
  }

  const tpe = teamParticipationError()
  if (tpe) {
    console.log('Team Participation error:', tpe)
  }

  const the = teamHealthError()
  if (the) {
    console.log('Team Health error:', the)
  }

  const tce = goToOfficeError()
  if (tce) {
    console.log('Team comfort error:', tce)
  }

  return (
    <>
      <Row>
        <Col sm="12" md="6" lg="4" className="mb-3">
          <Card style={{ overflow: 'hidden' }}>
            <CardBody>
              <CardTitle>Participation</CardTitle>
              {teamParticipationLoading() && <Spinner />}
              {teamParticipationError() && (
                <>
                  <div className="d-flex justify-content-center">
                    <FontAwesomeIcon
                      icon="exclamation-triangle"
                      color={chartColors.medium}
                      transform="grow-30"
                      className="mt-3"
                    />
                  </div>
                  <div className="mt-4 text-center fs--1">
                    <p>There was an error loading team participation data</p>
                  </div>
                </>
              )}
              {teamParticipationReady() && (
                <Doughnut
                  data={teamParticipationData()}
                  options={{
                    rotation: -1 * Math.PI,
                    circumference: Math.PI,
                    cutoutPercentage: 92,
                    legend: {
                      display: false,
                    },
                    plugins: {
                      doughnutlabel: {
                        labels: [
                          {
                            text: `${teamParticipationData().datasets[0].data[0]}`,
                            font: {
                              size: 36,
                              weight: 'bold',
                            },
                            color: chartColors.medium,
                          },
                          {
                            text: 'people who completed',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                          {
                            text: 'surveys in the last week',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                        ],
                      },
                    },
                  }}
                />
              )}
            </CardBody>
          </Card>
        </Col>
        <Col sm="12" md="6" lg="4" className="mb-3">
          <Card style={{ overflow: 'hidden' }}>
            <CardBody>
              <CardTitle>Health</CardTitle>
              {teamHealthLoading() && <Spinner />}
              {teamHealthError() && (
                <>
                  <div className="d-flex justify-content-center">
                    <FontAwesomeIcon
                      icon="exclamation-triangle"
                      color={chartColors.bad}
                      transform="grow-30"
                      className="mt-3"
                    />
                  </div>
                  <div className="mt-4 text-center fs--1">
                    <p>There was an error loading team health data</p>
                  </div>
                </>
              )}
              {teamHealthReady() && (
                <Doughnut
                  data={teamHealthData()}
                  options={{
                    rotation: -1 * Math.PI,
                    circumference: Math.PI,
                    cutoutPercentage: 92,
                    legend: {
                      display: false,
                    },
                    plugins: {
                      doughnutlabel: {
                        labels: [
                          {
                            text: `${teamHealthData().datasets[0].data[0]}`,
                            font: {
                              size: 36,
                              weight: 'bold',
                            },
                            color: chartColors.bad,
                          },
                          {
                            text: 'people',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                          {
                            text: 'with symptoms',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                        ],
                      },
                    },
                  }}
                />
              )}
            </CardBody>
          </Card>
        </Col>
        <Col sm="12" md="6" lg="4" className="mb-3">
          <Card style={{ overflow: 'hidden' }}>
            <CardBody>
              <CardTitle>Office Preference</CardTitle>
              {goToOfficeLoading() && <Spinner />}
              {goToOfficeError() && (
                <>
                  <div className="d-flex justify-content-center">
                    <FontAwesomeIcon
                      icon="exclamation-triangle"
                      color={colors[1]}
                      transform="grow-30"
                      className="mt-3"
                    />
                  </div>
                  <div className="mt-4 text-center fs--1">
                    <p>There was an error loading team health data</p>
                  </div>
                </>
              )}
              {goToOfficeReady() && (
                <Doughnut
                  data={goToOfficeData()}
                  options={{
                    rotation: -1 * Math.PI,
                    circumference: Math.PI,
                    cutoutPercentage: 92,
                    legend: {
                      display: false,
                    },
                    plugins: {
                      doughnutlabel: {
                        labels: [
                          {
                            text: `${goToOfficeData().datasets[0].data[0]}`,
                            font: {
                              size: 36,
                              weight: 'bold',
                            },
                            color: chartColors.good,
                          },
                          {
                            text: 'people want to',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                          {
                            text: 'visit the office',
                            font: {
                              size: 14,
                            },
                            color: themeColors.secondary,
                          },
                        ],
                      },
                    },
                  }}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default DailySnapshot
